import * as React from 'react';
import DocumentHeader from 'Components/DocumentHeader';
import DocumentWrapper from 'Components/DocumentWrapper';
import FormEditingHandler from 'Containers/FormEditingHandler';
import UPDRSForm from './Form';
import DBSEffectHistory from './HistoryRowData/DBSEffect';
import UPDRSIIIHistory from './HistoryRowData/III';
import FTMTRSHistory from './HistoryRowData/FTMTRS';
import LevodopatestHistory from './HistoryRowData/Levodopatest';
import UPDRSVHistory from './HistoryRowData/V';
import { filterByDocumentType, filterDocumentsByType } from 'Utility/documentHandling';
import { fm } from 'Components/FormatMessage';
import type { TRelevantDocumentType } from '../utils';

const UPDRSHistory = (
  documents: IDocumentWithTypeV2<TRelevantDocumentType>[],
  startEdit: StartEdit,
  view?: IView,
): React.JSX.Element => {
  const IIIDocs = filterByDocumentType(documents, 'updrs_iii');
  const VDocs = filterByDocumentType(documents, 'updrs_v');
  const LevodopaDocs = filterByDocumentType(documents, 'levodopatest');
  const DBSEffectDocs = filterByDocumentType(documents, 'dbsEffect');
  const ftmtrsDocs = filterByDocumentType(documents, 'ftmtrs');
  return (
    <React.Fragment>
      <UPDRSVHistory documents={VDocs} startEdit={startEdit} view={view} />
      <UPDRSIIIHistory documents={IIIDocs} startEdit={startEdit} view={view} />
      <FTMTRSHistory documents={ftmtrsDocs} startEdit={startEdit} view={view} />
      <LevodopatestHistory documents={LevodopaDocs} allDocuments={documents} startEdit={startEdit} view={view} />
      <DBSEffectHistory documents={DBSEffectDocs} allDocuments={documents} startEdit={startEdit} view={view} />
    </React.Fragment>
  );
};

const InfoText = ({ type }: { type: TRelevantDocumentType }): React.JSX.Element | undefined => {
  switch (type) {
    case 'updrs_iii':
      return fm('updrs.updrsIIIInfo', { ln: <br /> });
    case 'updrs_v':
    case 'levodopatest':
      return (
        <div>
          {fm('updrs.levodopaDescription.header_execution')}
          <ul>
            <li>{fm('updrs.levodopaDescription.1_patient12hWithoutMed')}</li>
            <li>{fm('updrs.levodopaDescription.2_motorEstimate')}</li>
            <li>{fm('updrs.levodopaDescription.3_patientMedication')}</li>
            <li>{fm('updrs.levodopaDescription.4_updrsiiiTest')}</li>
          </ul>
          <br />
          {fm('updrs.levodopaDescription.ps_info')}
        </div>
      );
    case 'dbsEffect':
      return (
        <div>
          {fm('updrs.dbsDescription.header_execution')}
          <ul>
            <li>{fm('updrs.dbsDescription.1_patient12hWithoutMed')}</li>
            <li>{fm('updrs.dbsDescription.2_motorEstimate')}</li>
            <li>{fm('updrs.dbsDescription.3_patientDBSsetting')}</li>
            <li>{fm('updrs.dbsDescription.4_updrsiiiTest')}</li>
          </ul>
          <br />
          {fm('updrs.dbsDescription.ps_info')}
        </div>
      );
    default:
      return undefined;
  }
};

const UPDRS = ({ documents }: IOwnProps): React.JSX.Element => (
  <FormEditingHandler name="updrs" documents={documents}>
    {(
      editing,
      startEdit,
      formData,
      view?: IView,
      _saveDraft?: (id: string) => (event: React.MouseEvent) => void,
      setSubDocuments?: (subDocuments: { name: string; id: string }[]) => void,
    ): React.JSX.Element => {
      const editingDocument = editing ? documents.find(d => d._id === editing) : undefined;
      const viewingDocument = view?.viewing ? documents.find(d => d._id === view.viewing) : undefined;
      const type = editingDocument?._type ?? viewingDocument?._type;
      return (
        <DocumentWrapper>
          <DocumentHeader
            name={'updrs'}
            headerId={
              {
                updrs_iii: 'updrs.updrsIII',
                ftmtrs: 'updrs.ftmtrs',
                updrs_v: 'updrs.updrsV',
                levodopatest: 'updrs.levodopatest',
                dbsEffect: 'updrs.dbsEffect',
                dbs: undefined,
                '': undefined,
              }[type ?? ''] || 'updrs.secondaryTitle'
            }
            editing={editing}
            editButtons={<div />}
            infoText={type ? <InfoText type={type} /> : undefined}
          />
          {editingDocument ? (
            <UPDRSForm
              formData={formData}
              document={editingDocument}
              documents={filterDocumentsByType(documents, [
                'updrs_iii',
                'dbsEffect',
                'levodopatest',
                'updrs_v',
                'ftmtrs',
              ])}
              setSubDocuments={setSubDocuments}
            />
          ) : null}
          {viewingDocument ? (
            <UPDRSForm
              formData={{ document: view?.document as IUPDRSIII }}
              document={viewingDocument}
              documents={filterDocumentsByType(documents, [
                'updrs_iii',
                'dbsEffect',
                'levodopatest',
                'updrs_v',
                'ftmtrs',
              ])}
              viewing={!!view?.viewing}
            />
          ) : null}
          {!editing && !view?.viewing ? UPDRSHistory(documents, startEdit, view) : undefined}
        </DocumentWrapper>
      );
    }}
  </FormEditingHandler>
);

interface IOwnProps {
  documents: IDocumentWithTypeV2<TRelevantDocumentType>[];
}

export default UPDRS;
